import toPairs from "lodash/toPairs"
import split from "lodash/split"
import map from "lodash/map"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import find from "lodash/find"
import filter from "lodash/filter"
import Head from "next/head"
import { useEffect, useState, useCallback, useMemo } from "react"
import Header from "component-lib/Header/HeaderContainer"
import Footer from "component-lib/Footer/FooterContainer"
import Link from "component-lib/Base/components/Link/Link"
import { getLanguageFromRoute } from "utils-lib/route"
import { getDeviceChannel } from "utils-lib/getDeviceChannel"
import { getWindow } from "utils-lib/getWindow"
import { LANG_OPTIONS_MAP, LOCALE_PATH_MAP } from "constants-lib/common"
import AppWrapper from "@components/AppWrapper/AppWrapperContainer"
import getContentUrl from "utils-lib/getContentUrl"
import { makeStyles } from "tss-react/mui"
import { handleEnterPress } from "utils-lib/handleEnterPress"
import { SEOMetaContext } from "contexts-lib/SeoMetaContext"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"

const HIDE_FEEDBACK_TAB_PATHS = {}

const HOMEPAGE_PATHS = {
  HOMEPAGE: `/`,
  HOMEPAGE_US_EN: `/us/en`,
  HOMEPAGE_US_EN_SLASH: `/us/en/`,
  HOMEPAGE_CA_EN: `/ca/en`,
  HOMEPAGE_CA_EN_SLASH: `/ca/en/`,
}

const window = getWindow()

const useStyles = makeStyles()(() => ({
  contentWrapper: {
    flexGrow: 1,
  },
  localModelLink: {
    position: "absolute",
    top: "4.5rem",
    left: "4.5rem",
    zIndex: 9999999999,
  },
  feedback: {
    "&:focus": {
      outline: `2px auto -webkit-focus-ring-color`,
    },
  },
}))

function getCanonicalUrl(modelPath, isHomepage, isSupportFAQ) {
  const language = getLanguageFromRoute(modelPath)
  const languagePath = LOCALE_PATH_MAP[language] || ``

  if (isHomepage) {
    switch (language) {
      case LANG_OPTIONS_MAP.EN_US:
        return (
          <link rel="canonical" href={`${process.env.NEXT_PUBLIC_ORIGIN}/`} />
        )
      case LANG_OPTIONS_MAP.EN_CA:
        return (
          <link
            rel="canonical"
            href={`${process.env.NEXT_PUBLIC_ORIGIN}${languagePath}`}
          />
        )
      case LANG_OPTIONS_MAP.FR_CA:
        return (
          <link
            rel="canonical"
            href={`${process.env.NEXT_PUBLIC_ORIGIN}${languagePath}`}
          />
        )
      default:
        return (
          <link rel="canonical" href={`${process.env.NEXT_PUBLIC_ORIGIN}/`} />
        )
    }
  }

  if (isSupportFAQ && window.location.pathname !== modelPath) {
    const canonicalHref = `${process.env.NEXT_PUBLIC_ORIGIN}${modelPath}`
    const updatedHref = canonicalHref.replace(
      /\/support.*/,
      window.location.pathname.match(/\/support.*/),
    )

    return <link rel="canonical" href={updatedHref} />
  }

  return (
    <link
      rel="canonical"
      href={`${
        process.env.NEXT_PUBLIC_ORIGIN // default to origin from config on the server since there is no window object
      }${modelPath}`}
    />
  )
}

function getAlternateLinks(model, isSupportFAQ, showSelfReference = false) {
  const language = getLanguageFromRoute(model.cqPath)

  if (isSupportFAQ && window?.location?.pathname !== model.cqPath) {
    return map(toPairs(model.href), ([href, lang]) => {
      const alternateLanguage = getLanguageFromRoute(href)

      const shouldInclude = showSelfReference
        ? href && lang
        : href && lang && alternateLanguage !== language

      if (shouldInclude) {
        const updatedHref = href.replace(
          /\/support.*/,
          window.location.pathname.match(/\/support.*/),
        )

        return (
          <link key={lang} rel="alternate" href={updatedHref} hrefLang={lang} />
        )
      }

      return null
    })
  }

  return map(toPairs(model.href), ([href, lang]) => {
    const alternateLanguage = getLanguageFromRoute(href)

    const shouldInclude = showSelfReference
      ? href && lang
      : href && lang && alternateLanguage !== language

    if (shouldInclude) {
      return <link key={lang} rel="alternate" href={href} hrefLang={lang} />
    }
    return null
  })
}

export default function Layout({
  children,
  model,
  dataPageData,
  isHomepage = false,
}) {
  const [haveFeedbackStylesLoaded, setHaveFeedbackStylesLoaded] =
    useState(false)
  const [shouldShowFooter, setShouldShowFooter] = useState(false)

  const { LDFFRemoveSelfReferencingHreflang } = useFeatureFlags([
    "LDFFRemoveSelfReferencingHreflang",
  ])

  const [seo, setSeo] = useState({
    title: model.title || ``,
    description: model.metaSeoDescription || ``,
  })

  const updateSEOMeta = useCallback(
    ({ title: newTitle = null, description: newDescription = null }) => {
      setSeo((prev) => ({
        title: newTitle ?? prev.title,
        description: newDescription ?? prev.description,
      }))
    },
    [],
  )

  const isSupportFAQ = useMemo(() => {
    // During SSR, use model.cqPath; on client, use window.location.pathname
    const pathname = window?.location?.pathname || model.cqPath
    const result = /\/support\/faqs/.test(pathname)

    return result
  }, [model.cqPath])

  const canonicalEl = useMemo(
    () => getCanonicalUrl(model.cqPath, isHomepage, isSupportFAQ),
    [model.cqPath, isHomepage, isSupportFAQ],
  )

  const alternateEls = useMemo(
    () =>
      getAlternateLinks(
        model,
        isSupportFAQ,
        LDFFRemoveSelfReferencingHreflang ?? false,
      ),
    [model, isSupportFAQ, LDFFRemoveSelfReferencingHreflang],
  )

  const isFrPage = getLanguageFromRoute() === LANG_OPTIONS_MAP.FR_CA
  const isErrorPage =
    model.cqPath?.endsWith("/errors/500") ||
    model.cqPath?.endsWith("/errors/404")

  const { classes } = useStyles()

  useEffect(() => {
    !isFrPage && loadFeedbackStyleSheetAsync()

    const isPreferencesCenter = window.location.pathname.includes(
      `mywm/user/preferences/`,
    )
    const isManageContacts =
      window.location.pathname.includes(`mywm/user/contacts/`)
    const delay = isPreferencesCenter || isManageContacts ? 300 : 100

    // defer rendering of footer to smoothe out CSR components such as ManageContacts and CommunicationPreferences
    setTimeout(() => setShouldShowFooter(true), delay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (haveFeedbackStylesLoaded) {
      try {
        window.feedbackInit()
      } catch (e) {
        console.error(`Error calling feedbackInit`, e)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [haveFeedbackStylesLoaded])

  useEffect(() => {
    setSeo((prev) => ({
      ...prev,
      title: model.title || "",
      description: model.metaSeoDescription || "",
    }))
  }, [model])

  async function loadFeedbackStyleSheetAsync() {
    const getContentUrl = await import("utils-lib/getContentUrl").then(
      (mod) => mod.default,
    )

    const src = getContentUrl(
      `/content/dam/wm/concentrix/feedback/css/feedback-custom.css`,
    )

    if (document.createStyleSheet) {
      document.createStyleSheet(src)
    } else {
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = src

      document.getElementsByTagName("head")[0].appendChild(link)
    }

    setTimeout(() => setHaveFeedbackStylesLoaded(true), 200)
  }

  function getCNXFeedbackTab() {
    const deviceSource = getDeviceChannel() === `M` ? ` m` : ``
    return (
      getFeedbackTabVisibility() && (
        <>
          <div
            tabIndex={0}
            className={`a-taggable feedback-container feedback-right slide-offset-top ${classes.feedback}`}
            // eslint-disable-next-line react/no-unknown-property
            analytics={`cnx feedback tab ${getPagenameForFeedback()}${deviceSource}`}
            id="feedback-number-0"
            role="link"
            style={{ visibility: `visible` }}
            onClick={() => window.openFeedbackModal()}
            onKeyDown={(e) => {
              handleEnterPress(e, () => window.openFeedbackModal())
            }}
          >
            <div className="feedback-inner-wrapper">
              <div
                id="feedback_tab"
                style={{ display: isErrorPage ? "none" : "block" }}
              >
                <span>Feedback</span>
              </div>
            </div>
          </div>
          <div
            id="feedback_modal"
            onClick={() => {
              // eslint-disable-next-line react/no-this-in-sfc
              this.style.display = "none"
            }}
          />
        </>
      )
    )
  }

  function getFeedbackTabVisibility() {
    const isFeedbackHiddenPage = filter(
      HIDE_FEEDBACK_TAB_PATHS,
      (pagePath) => window.location.pathname === pagePath,
    )

    return !isFrPage && isEmpty(isFeedbackHiddenPage)
  }

  function getPagenameForFeedback() {
    if (find(HOMEPAGE_PATHS, (path) => window.location.pathname === path)) {
      return `homepage`
    }
    const splitBySlash = split(window.location.pathname, `/`)
    const length = get(splitBySlash, `length`, 0)
    return splitBySlash.length === 0
      ? `blank`
      : get(splitBySlash, `[${length - 1}]`, ``)
  }

  const hideHeaderFooter = model.hideHeaderFooter === "yes"
  const defaultTabIcon = `/content/dam/wm/icons/favicon.ico`

  return (
    <AppWrapper model={model}>
      <Head>
        <title>{seo.title}</title>
        <meta name="title" content={seo.title} />
        <meta name="L1" content={model.l1PageName} />
        <meta name="description" content={seo.description} />
        <meta name="robots" content={model.metaSeoRobots} />
        {model.primaryGisId && (
          <meta name="PrimaryGisId" content={model.primaryGisId} />
        )}
        {canonicalEl}
        {alternateEls}
        {model.serviceSchema && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(model.serviceSchema)
                .replace(/</g, "\\u003c")
                .replace(/&quot;/g, '"'),
            }}
          />
        )}
        {model.faqSchema && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(model.faqSchema)
                .replace(/</g, "\\u003c")
                .replace(/&quot;/g, '"'),
            }}
          />
        )}
        {model.seoSchemaJson && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(model.seoSchemaJson)
                .replace(/</g, "\\u003c")
                .replace(/&quot;/g, '"'),
            }}
          />
        )}
        <link
          rel="icon"
          href={getContentUrl(model?.tabIcon ?? defaultTabIcon)}
        />
      </Head>
      {!hideHeaderFooter && (
        <Header
          location={window.location}
          navigation={get(model, `navigation`, ``)}
          // skip the headerRender checks since using SSR/SSG
          setHeaderStatus={() => {}}
          isHeaderRendered
          pageLocales={model.pageLocales}
          hasFrPage={model.hasFrPage}
          ssrModel={model}
          dataPageData={dataPageData}
        />
      )}
      <main className={classes.contentWrapper}>
        {process.env.NEXT_PUBLIC_USE_LOCAL_MODEL && (
          <Link href="/us/en/local" className={classes.localModelLink}>
            Local Model
          </Link>
        )}
        <div
          id="ssg-timestamp"
          data-timestamp={model?.ssgTimestamp ?? undefined}
          style={{ display: "none" }}
        />
        <SEOMetaContext.Provider value={{ updateSEOMeta }}>
          {children}
        </SEOMetaContext.Provider>
      </main>
      {!hideHeaderFooter && shouldShowFooter && (
        <Footer
          pageLocales={model.pageLocales}
          hasFrPage={model.hasFrPage}
          dataPageData={dataPageData}
        />
      )}
      {haveFeedbackStylesLoaded && getCNXFeedbackTab()}
    </AppWrapper>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// Layout.propTypes = {
//   children: PropTypes.node.isRequired,
//   model: PropTypes.object.isRequired,
//   dataPageData: PropTypes.object.isRequired,
//   userAccount: PropTypes.object.isRequired,
// }
